// ============================================
// 🔔 토스트 알림 시스템
// ============================================
import React, { createContext, useContext, useState, useCallback } from 'react';

const ToastContext = createContext();

// 토스트 타입
const TOAST_TYPES = {
  success: {
    icon: '✅',
    color: '#4CAF50',
    bgColor: '#e8f5e9'
  },
  error: {
    icon: '❌',
    color: '#f44336',
    bgColor: '#ffebee'
  },
  warning: {
    icon: '⚠️',
    color: '#FF9800',
    bgColor: '#fff3e0'
  },
  info: {
    icon: 'ℹ️',
    color: '#2196F3',
    bgColor: '#e3f2fd'
  }
};

// Provider 컴포넌트
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 3000) => {
    const id = Date.now();
    const toast = { id, message, type, duration };
    
    setToasts(prev => [...prev, toast]);

    // 자동 제거
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // 편의 함수들
  const toast = {
    success: (msg, duration) => addToast(msg, 'success', duration),
    error: (msg, duration) => addToast(msg, 'error', duration),
    warning: (msg, duration) => addToast(msg, 'warning', duration),
    info: (msg, duration) => addToast(msg, 'info', duration)
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

// 토스트 컨테이너
function ToastContainer({ toasts, onRemove }) {
  if (toasts.length === 0) return null;

  return (
    <div style={styles.container}>
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          toast={toast}
          onClose={() => onRemove(toast.id)}
        />
      ))}
    </div>
  );
}

// 개별 토스트
function Toast({ toast, onClose }) {
  const config = TOAST_TYPES[toast.type] || TOAST_TYPES.info;

  return (
    <div
      style={{
        ...styles.toast,
        backgroundColor: config.bgColor,
        borderLeft: `4px solid ${config.color}`
      }}
    >
      <div style={styles.icon}>{config.icon}</div>
      <div style={styles.message}>{toast.message}</div>
      <button onClick={onClose} style={styles.closeButton}>
        ✕
      </button>
    </div>
  );
}

// Hook
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}

const styles = {
  container: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    zIndex: 10000,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    maxWidth: '400px'
  },
  toast: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    animation: 'slideIn 0.3s ease-out',
    minWidth: '300px'
  },
  icon: {
    fontSize: '20px',
    flexShrink: 0
  },
  message: {
    flex: 1,
    fontSize: '14px',
    color: '#333',
    lineHeight: '1.4'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '18px',
    cursor: 'pointer',
    color: '#999',
    padding: '0',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    transition: 'background-color 0.2s'
  }
};

// CSS 애니메이션을 App.css에 추가할 내용
export const toastCSS = `
@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideOut {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}
`;